import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  UncontrolledCollapse,
  Alert,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import Select from 'react-select';
import { convertFromRaw } from 'draft-js';
import Swal from 'sweetalert2';
import ExcelJS from 'exceljs';
import { renderDraftContent, draftRawToPlainText } from '../../util/draftToHtml';
import { extractImageUrls } from '../../util/draftEntityCleanup';
import { customDropdownWithBorder } from '../../util/reactCustomStyles';
import { fetchQuestions, deleteQuestion, updateQuestionOrder } from '../../redux/QuestionBank/QuestionsAction';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../redux/QuestionBank/SubjectsUnitsAction';
import { fetchPassages } from '../../redux/QuestionBank/PassageAction';
import { useDebounce } from '../../util/useDebounce';
import { formatDate } from '../../util/formatDate';
import { getSpecialty } from 'config/specialtyConfig';
import { getDisplayName } from '../../util/specialtyDisplayHelper';

const QuestionsList = ({ onEditQuestion }) => {
  const dispatch = useDispatch();

  // Redux state
  const { questions, loading } = useSelector(state => state.questionBankQuestions);
  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);
  const { topics } = useSelector(state => state.questionBankTopics);
  const { passages } = useSelector(state => state.questionBankPassages);
  const activeSpecialty = useSelector(state => state.specialty.activeSpecialty);

  // Filter state
  const [filterSubject, setFilterSubject] = useState('');
  const [filterUnit, setFilterUnit] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterQuality, setFilterQuality] = useState('');
  const [filterNoTopic, setFilterNoTopic] = useState(false);
  const [filterHasImage, setFilterHasImage] = useState(false);
  const [filterPercentCorrectMode, setFilterPercentCorrectMode] = useState('range');
  const [filterPercentCorrectMin, setFilterPercentCorrectMin] = useState('');
  const [filterPercentCorrectMax, setFilterPercentCorrectMax] = useState('');
  const [filterPercentCorrectValue, setFilterPercentCorrectValue] = useState('');
  const [filterMinAnswered, setFilterMinAnswered] = useState('');

  // Sort state
  const [sortBy, setSortBy] = useState('newest');

  // Search state
  const [passageSearch, setPassageSearch] = useState('');
  const [standaloneSearch, setStandaloneSearch] = useState('');
  const debouncedPassageSearch = useDebounce(passageSearch, 300);
  const debouncedStandaloneSearch = useDebounce(standaloneSearch, 300);

  // Expanded state for tracking which questions are expanded
  const [expandedQuestions, setExpandedQuestions] = useState({});

  // Expanded state for tracking which passages are expanded
  const [expandedPassages, setExpandedPassages] = useState({});

  // Passage height state for resizable preview
  const [passageHeights, setPassageHeights] = useState({});

  // Drag and drop state
  const [draggedQuestion, setDraggedQuestion] = useState(null);
  const [dragOverQuestion, setDragOverQuestion] = useState(null);

  // Pagination for passages
  const [passagePage, setPassagePage] = useState(1);
  const passagesPerPage = 5;

  // Pagination for standalone questions
  const [standalonePage, setStandalonePage] = useState(1);
  const standalonePerPage = 10;

  useEffect(() => {
    dispatch(fetchQuestions());
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
    dispatch(fetchPassages());
  }, [dispatch, activeSpecialty]);

  // M4: Reset pagination when filters or underlying data change
  useEffect(() => {
    setPassagePage(1);
    setStandalonePage(1);
  }, [filterSubject, filterUnit, filterDifficulty, filterQuality, filterNoTopic, filterPercentCorrectMode, filterPercentCorrectMin, filterPercentCorrectMax, filterPercentCorrectValue, filterMinAnswered, questions]);

  // Check if any filters are active
  const hasActiveFilters = filterSubject || filterUnit || filterDifficulty || filterQuality || filterNoTopic || filterHasImage || filterMinAnswered;

  // Clear all filters
  const handleClearFilters = () => {
    setFilterSubject('');
    setFilterUnit('');
    setFilterDifficulty('');
    setFilterQuality('');
    setFilterNoTopic(false);
    setFilterHasImage(false);
    setFilterPercentCorrectMode('range');
    setFilterPercentCorrectMin('');
    setFilterPercentCorrectMax('');
    setFilterPercentCorrectValue('');
    setFilterMinAnswered('');
    setPassageSearch('');
    setStandaloneSearch('');
    setSortBy('newest');
    setPassagePage(1);
    setStandalonePage(1);
  };

  // Filtered units based on selected subject
  const filteredUnits = filterSubject
    ? units.filter(unit => unit.subjectId === filterSubject)
    : units;

  // Check if a question contains any images in its content fields
  const questionHasImage = (question) => {
    if (extractImageUrls(question.questionText).length > 0) return true;
    if (extractImageUrls(question.explanationText).length > 0) return true;
    if (question.choices?.some(choice => extractImageUrls(choice.text).length > 0)) return true;
    return false;
  };

  // Filter questions
  const filteredQuestions = questions.filter(question => {
    if (filterSubject && question.subjectId !== filterSubject) return false;
    if (filterUnit && question.unitId !== filterUnit) return false;
    if (filterDifficulty && question.difficulty !== filterDifficulty) return false;
    if (filterQuality && question.quality !== filterQuality) return false;
    if (filterNoTopic && question.topicId) return false; // Only show questions without topic
    if (filterHasImage && !questionHasImage(question)) return false; // Only show questions with images
    // Min answered filter — apply independently of % correct
    if (filterMinAnswered !== '') {
      const minAns = parseInt(filterMinAnswered, 10);
      if (!isNaN(minAns) && (question.timesAnswered || 0) < minAns) return false;
    }
    if (filterPercentCorrectMode) {
      const answered = question.timesAnswered || 0;
      if (answered === 0) return false;
      const pct = ((question.timesCorrect || 0) / answered) * 100;
      if (filterPercentCorrectMode === 'range') {
        if (filterPercentCorrectMin !== '') {
          const minPct = parseFloat(filterPercentCorrectMin);
          if (!isNaN(minPct) && pct < minPct) return false;
        }
        if (filterPercentCorrectMax !== '') {
          const maxPct = parseFloat(filterPercentCorrectMax);
          if (!isNaN(maxPct) && pct > maxPct) return false;
        }
      } else {
        const val = parseFloat(filterPercentCorrectValue);
        if (!isNaN(val)) {
          if (filterPercentCorrectMode === 'less' && pct >= val) return false;
          if (filterPercentCorrectMode === 'greater' && pct <= val) return false;
        }
      }
    }
    return true;
  });

  // Group questions by passage
  const groupedQuestions = React.useMemo(() => {
    const passageGroups = {};
    const standaloneQuestions = [];

    filteredQuestions.forEach(question => {
      if (question.passageId) {
        if (!passageGroups[question.passageId]) {
          passageGroups[question.passageId] = {
            passage: passages.find(p => p.passageId === question.passageId),
            questions: []
          };
        }
        passageGroups[question.passageId].questions.push(question);
      } else {
        standaloneQuestions.push(question);
      }
    });

    return { passageGroups, standaloneQuestions };
  }, [filteredQuestions, passages]);

  // Sort passages
  const sortedPassageGroups = React.useMemo(() => {
    const groups = Object.values(groupedQuestions.passageGroups).filter(g => g.passage);
    switch (sortBy) {
      case 'newest':
        return groups.sort((a, b) => new Date(b.passage.createdAt) - new Date(a.passage.createdAt));
      case 'oldest':
        return groups.sort((a, b) => new Date(a.passage.createdAt) - new Date(b.passage.createdAt));
      case 'title_asc':
        return groups.sort((a, b) => (a.passage.title || '').localeCompare(b.passage.title || ''));
      case 'title_desc':
        return groups.sort((a, b) => (b.passage.title || '').localeCompare(a.passage.title || ''));
      case 'most_questions':
        return groups.sort((a, b) => b.questions.length - a.questions.length);
      case 'least_questions':
        return groups.sort((a, b) => a.questions.length - b.questions.length);
      default:
        return groups;
    }
  }, [groupedQuestions.passageGroups, sortBy]);

  // Sort standalone questions
  const sortedStandaloneQuestions = React.useMemo(() => {
    const questions = [...groupedQuestions.standaloneQuestions];
    switch (sortBy) {
      case 'newest':
        return questions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'oldest':
        return questions.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'title_asc':
      case 'title_desc':
        // For standalone, sort by question code
        return sortBy === 'title_asc'
          ? questions.sort((a, b) => (a.questionCode || '').localeCompare(b.questionCode || ''))
          : questions.sort((a, b) => (b.questionCode || '').localeCompare(a.questionCode || ''));
      case 'no_topic_first':
        // Questions without topic come first, then by newest
        return questions.sort((a, b) => {
          const aHasTopic = a.topicId ? 1 : 0;
          const bHasTopic = b.topicId ? 1 : 0;
          if (aHasTopic !== bHasTopic) return aHasTopic - bHasTopic;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
      default:
        return questions;
    }
  }, [groupedQuestions.standaloneQuestions, sortBy]);

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.subjectId === subjectId);
    return subject ? getDisplayName(subject, activeSpecialty) : 'Unknown';
  };

  const getUnitName = (unitId) => {
    const unit = units.find(u => u.unitId === unitId);
    return unit ? getDisplayName(unit, activeSpecialty) : 'Unknown';
  };

  const getTopicName = (topicId) => {
    if (!topicId) return null;
    const topic = topics.find(t => t.topicId === topicId);
    return topic ? getDisplayName(topic, activeSpecialty) : null;
  };

  const getPassageTitle = (passageId) => {
    const passage = passages.find(p => p.passageId === passageId);
    return passage ? passage.title : 'Unknown Passage';
  };

  const getPassageByQuestion = (question) => {
    if (!question.passageId) return null;
    return passages.find(p => p.passageId === question.passageId);
  };

  const handleDelete = async (questionId) => {
    const confirm = await Swal.fire({
      title: 'Archive Question?',
      text: 'This will archive the question and remove it from all question lists and test creation. Existing tests are not affected.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, archive it'
    });

    if (confirm.isConfirmed) {
      const result = await dispatch(deleteQuestion(questionId));
      if (result.success) {
        Swal.fire('Archived!', 'Question has been archived.', 'success');
        dispatch(fetchQuestions());
      } else {
        Swal.fire('Error', result.error, 'error');
      }
    }
  };

  // M10: Use shared helper (with M9 DOMPurify sanitization)
  const renderQuestionPreview = (rawContent) => renderDraftContent(rawContent);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'danger';
      default: return 'secondary';
    }
  };

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Export questions to Excel
  const handleExportQuestions = async () => {
    if (filteredQuestions.length === 0) {
      Swal.fire('No Questions', 'There are no questions to export with the current filters.', 'warning');
      return;
    }

    setIsExporting(true);

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = getSpecialty(activeSpecialty).name + ' Prep';
      workbook.created = new Date();

      const worksheet = workbook.addWorksheet('Questions');

      // Define columns
      worksheet.columns = [
        { header: 'Question Code', key: 'questionCode', width: 20 },
        { header: 'Subject', key: 'subject', width: 25 },
        { header: 'Unit', key: 'unit', width: 25 },
        { header: 'Topic', key: 'topic', width: 25 },
        { header: 'Passage Title', key: 'passageTitle', width: 30 },
        { header: 'Question Text', key: 'questionText', width: 60 },
        ...Array.from({ length: 10 }, (_, i) => ({
          header: `Choice ${String.fromCharCode(65 + i)}`,
          key: `choice${String.fromCharCode(65 + i)}`,
          width: 40
        })),
        { header: 'Correct Answer', key: 'correctAnswer', width: 15 },
        { header: 'Explanation', key: 'explanation', width: 60 },
        { header: 'Difficulty', key: 'difficulty', width: 12 },
        { header: 'Tags', key: 'tags', width: 30 },
        { header: 'Created At', key: 'createdAt', width: 20 },
        { header: 'Times Answered', key: 'timesAnswered', width: 15 },
        { header: 'Times Correct', key: 'timesCorrect', width: 15 }
      ];

      // Style header row
      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4A90D9' }
      };
      worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };

      // Add data rows
      filteredQuestions.forEach(question => {
        const passage = question.passageId ? passages.find(p => p.passageId === question.passageId) : null;

        // Get correct answer letters
        const correctLetters = (question.correctAnswer || []).map(choiceId => {
          const idx = (question.choices || []).findIndex(c => c.choiceId === choiceId);
          return idx >= 0 ? String.fromCharCode(65 + idx) : '';
        }).filter(Boolean).join(', ');

        worksheet.addRow({
          questionCode: question.questionCode || '',
          subject: getSubjectName(question.subjectId),
          unit: getUnitName(question.unitId),
          topic: getTopicName(question.topicId) || '',
          passageTitle: passage ? passage.title : '',
          questionText: draftRawToPlainText(question.questionText),
          ...Object.fromEntries(Array.from({ length: 10 }, (_, i) => [
            `choice${String.fromCharCode(65 + i)}`,
            question.choices?.[i] ? draftRawToPlainText(question.choices[i].text) : ''
          ])),
          correctAnswer: correctLetters,
          explanation: draftRawToPlainText(question.explanationText),
          difficulty: question.difficulty || '',
          tags: (question.tags || []).join(', '),
          createdAt: question.createdAt ? formatDate(question.createdAt) : '',
          timesAnswered: question.timesAnswered || 0,
          timesCorrect: question.timesCorrect || 0
        });
      });

      // Enable text wrapping for content columns
      ['questionText', ...Array.from({ length: 10 }, (_, i) => `choice${String.fromCharCode(65 + i)}`), 'explanation'].forEach(key => {
        const col = worksheet.getColumn(key);
        col.alignment = { wrapText: true, vertical: 'top' };
      });

      // Generate file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      const filterInfo = filterSubject ? `_${getSubjectName(filterSubject).replace(/[^a-zA-Z0-9]/g, '_')}` : '';
      link.download = `PMR_Questions_Export${filterInfo}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.fire('Export Complete', `Successfully exported ${filteredQuestions.length} question(s) to Excel.`, 'success');
    } catch (error) {
      console.error('Export error:', error);
      Swal.fire('Export Failed', 'An error occurred while exporting questions. Please try again.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const getQualityColor = (quality) => {
    return quality === 'excellent' ? 'success' : 'info';
  };

  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const handlePassageResize = (passageId, e) => {
    e.preventDefault();
    const startY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    const startHeight = passageHeights[passageId] || 200;

    const onMove = (moveEvent) => {
      const currentY = moveEvent.type === 'touchmove' ? moveEvent.touches[0].clientY : moveEvent.clientY;
      const newHeight = Math.max(100, Math.min(600, startHeight + (currentY - startY)));
      setPassageHeights(prev => ({ ...prev, [passageId]: newHeight }));
    };

    const onEnd = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove);
    document.addEventListener('touchend', onEnd);
  };

  const togglePassage = (passageId) => {
    setExpandedPassages(prev => ({
      ...prev,
      [passageId]: !prev[passageId]
    }));
  };

  const getChoiceLabel = (index) => {
    return String.fromCharCode(65 + index); // 65 is ASCII for 'A'
  };

  // Drag and drop handlers for question reordering
  const handleDragStart = (e, questionId, passageId) => {
    setDraggedQuestion({ questionId, passageId });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', questionId);
  };

  const handleDragOver = (e, questionId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverQuestion !== questionId) {
      setDragOverQuestion(questionId);
    }
  };

  const handleDragLeave = () => {
    setDragOverQuestion(null);
  };

  const handleDrop = async (e, targetQuestionId, passageId) => {
    e.preventDefault();
    setDragOverQuestion(null);

    if (!draggedQuestion || draggedQuestion.passageId !== passageId) {
      setDraggedQuestion(null);
      return;
    }

    const group = groupedQuestions.passageGroups[passageId];
    if (!group) return;

    const currentQuestions = [...group.questions].sort((a, b) => (a.passageOrder || 0) - (b.passageOrder || 0));
    const draggedIndex = currentQuestions.findIndex(q => q.questionId === draggedQuestion.questionId);
    const targetIndex = currentQuestions.findIndex(q => q.questionId === targetQuestionId);

    if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) {
      setDraggedQuestion(null);
      return;
    }

    // Reorder questions
    const [removed] = currentQuestions.splice(draggedIndex, 1);
    currentQuestions.splice(targetIndex, 0, removed);

    const orderedIds = currentQuestions.map(q => q.questionId);

    // Update order in database
    const result = await dispatch(updateQuestionOrder(passageId, orderedIds));
    if (result.success) {
      dispatch(fetchQuestions());
    } else {
      Swal.fire('Error', 'Failed to update question order', 'error');
    }

    setDraggedQuestion(null);
  };

  const handleDragEnd = () => {
    setDraggedQuestion(null);
    setDragOverQuestion(null);
  };

  // Filter and paginate passages by search (using sorted order)
  const filteredPassageArray = React.useMemo(() => {
    // Get passage IDs in sorted order
    const allPassageIds = sortedPassageGroups.map(g => g.passage.passageId);
    if (!debouncedPassageSearch.trim()) return allPassageIds;

    const searchLower = debouncedPassageSearch.toLowerCase().trim();
    return allPassageIds.filter(passageId => {
      const group = groupedQuestions.passageGroups[passageId];
      const passage = group?.passage;
      if (!passage) return false;

      // Search by passage title
      if (passage.title?.toLowerCase().includes(searchLower)) return true;

      // Search by passage text
      try {
        const contentState = convertFromRaw(passage.passageText);
        if (contentState.getPlainText().toLowerCase().includes(searchLower)) return true;
      } catch (e) {}

      // Search by subject
      const subjectName = getSubjectName(passage.subjectId);
      if (subjectName?.toLowerCase().includes(searchLower)) return true;

      // Search by unit
      const unitName = getUnitName(passage.unitId);
      if (unitName?.toLowerCase().includes(searchLower)) return true;

      // Search in questions
      const hasMatchingQuestion = group.questions.some(q => {
        // Search by question code
        if (q.questionCode?.toLowerCase().includes(searchLower)) return true;
        // Search by question text
        try {
          const qContentState = convertFromRaw(q.questionText);
          if (qContentState.getPlainText().toLowerCase().includes(searchLower)) return true;
        } catch (e) {}
        // Search by topic
        const topicName = getTopicName(q.topicId);
        if (topicName?.toLowerCase().includes(searchLower)) return true;
        // Search by difficulty
        if (q.difficulty?.toLowerCase().includes(searchLower)) return true;
        // Search by quality
        if (q.quality?.toLowerCase().includes(searchLower)) return true;
        // Search by tags
        if (q.tags?.some(tag => tag.toLowerCase().includes(searchLower))) return true;
        return false;
      });
      return hasMatchingQuestion;
    });
  }, [sortedPassageGroups, groupedQuestions.passageGroups, debouncedPassageSearch, subjects, units, topics]);

  const paginatedPassages = React.useMemo(() => {
    const startIndex = (passagePage - 1) * passagesPerPage;
    const endIndex = startIndex + passagesPerPage;
    return filteredPassageArray.slice(startIndex, endIndex);
  }, [filteredPassageArray, passagePage, passagesPerPage]);

  const expandAllPassages = () => {
    const allExpanded = { ...expandedPassages };
    paginatedPassages.forEach(passageId => {
      allExpanded[passageId] = true;
    });
    setExpandedPassages(allExpanded);
  };

  const collapseAllPassages = () => {
    const collapsed = { ...expandedPassages };
    paginatedPassages.forEach(passageId => {
      delete collapsed[passageId];
    });
    setExpandedPassages(collapsed);
  };

  const totalPassagePages = Math.ceil(filteredPassageArray.length / passagesPerPage);
  const passageCount = filteredPassageArray.length;

  // M5: Filter standalone questions by search while preserving sort order
  const filteredStandaloneQuestions = React.useMemo(() => {
    if (!debouncedStandaloneSearch.trim()) return sortedStandaloneQuestions;

    const searchLower = debouncedStandaloneSearch.toLowerCase().trim();
    return sortedStandaloneQuestions.filter(question => {
      // Search by code
      if (question.questionCode?.toLowerCase().includes(searchLower)) return true;
      // Search by question text
      try {
        const contentState = convertFromRaw(question.questionText);
        if (contentState.getPlainText().toLowerCase().includes(searchLower)) return true;
      } catch (e) {}
      // Search by subject
      const subjectName = getSubjectName(question.subjectId);
      if (subjectName?.toLowerCase().includes(searchLower)) return true;
      // Search by topic
      const topicName = getTopicName(question.topicId);
      if (topicName?.toLowerCase().includes(searchLower)) return true;
      // Search by unit
      const unitName = getUnitName(question.unitId);
      if (unitName?.toLowerCase().includes(searchLower)) return true;
      // Search by difficulty
      if (question.difficulty?.toLowerCase().includes(searchLower)) return true;
      // Search by quality
      if (question.quality?.toLowerCase().includes(searchLower)) return true;
      // Search by tags
      if (question.tags?.some(tag => tag.toLowerCase().includes(searchLower))) return true;
      return false;
    });
  }, [sortedStandaloneQuestions, debouncedStandaloneSearch, subjects, units, topics]);

  const paginatedStandaloneQuestions = React.useMemo(() => {
    const startIndex = (standalonePage - 1) * standalonePerPage;
    const endIndex = startIndex + standalonePerPage;
    return filteredStandaloneQuestions.slice(startIndex, endIndex);
  }, [filteredStandaloneQuestions, standalonePage, standalonePerPage]);

  const totalStandalonePages = Math.ceil(filteredStandaloneQuestions.length / standalonePerPage);

  return (
    <Card className="shadow mt-4">
      <CardHeader className="bg-transparent">
        <Row className="align-items-center">
          <Col xs="12" sm="6" className="mb-2 mb-sm-0">
            <h3 className="mb-0">Questions List</h3>
          </Col>
          <Col xs="12" sm="6" className="text-sm-right d-flex justify-content-start justify-content-sm-end align-items-center">
            <Badge color="info" pill className="mr-3">
              {filteredQuestions.length} Question{filteredQuestions.length !== 1 ? 's' : ''}
            </Badge>
            <Button
              color="success"
              size="sm"
              onClick={handleExportQuestions}
              disabled={isExporting || filteredQuestions.length === 0}
            >
              {isExporting ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  Exporting...
                </>
              ) : (
                <>
                  <i className="fas fa-file-excel mr-2"></i>
                  Export to Excel
                </>
              )}
            </Button>
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        {/* Filters */}
        <Row className="mb-2">
          <Col xs="12" md="6" className="mb-3 mb-md-0">
            <FormGroup className="mb-0">
              <Label className="form-control-label">
                Filter by Subject
              </Label>
              <Select
                options={[
                  { value: '', label: 'All Subjects' },
                  ...subjects.map(subject => ({
                    value: subject.subjectId,
                    label: getDisplayName(subject, activeSpecialty)
                  }))
                ]}
                value={
                  filterSubject
                    ? {
                        value: filterSubject,
                        label: (() => {
                          const s = subjects.find(s => s.subjectId === filterSubject);
                          return getDisplayName(s, activeSpecialty);
                        })()
                      }
                    : { value: '', label: 'All Subjects' }
                }
                onChange={(option) => {
                  setFilterSubject(option.value);
                  setFilterUnit('');
                }}
                styles={customDropdownWithBorder}
                placeholder="All Subjects"
              />
            </FormGroup>
          </Col>
          <Col xs="12" md="6" className="mb-3 mb-md-0">
            <FormGroup className="mb-0">
              <Label className="form-control-label">
                Filter by Unit
              </Label>
              <Select
                options={[
                  { value: '', label: 'All Units' },
                  ...filteredUnits.map(unit => ({
                    value: unit.unitId,
                    label: getDisplayName(unit, activeSpecialty)
                  }))
                ]}
                value={
                  filterUnit
                    ? {
                        value: filterUnit,
                        label: (() => {
                          const u = units.find(u => u.unitId === filterUnit);
                          return getDisplayName(u, activeSpecialty);
                        })()
                      }
                    : { value: '', label: 'All Units' }
                }
                onChange={(option) => setFilterUnit(option.value)}
                styles={customDropdownWithBorder}
                placeholder="All Units"
                isDisabled={!filterSubject}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row className="mb-3 mt-3">
          <Col xs="12" md="6" className="d-flex align-items-center">
            <div className="custom-control custom-checkbox ml-0">
              <input
                type="checkbox"
                className="custom-control-input"
                id="filterNoTopic"
                checked={filterNoTopic}
                onChange={(e) => setFilterNoTopic(e.target.checked)}
              />
              <label className="custom-control-label" htmlFor="filterNoTopic" style={{ cursor: 'pointer' }}>
                Show only questions without topic
              </label>
            </div>
          </Col>
          <Col xs="12" md="6" className="d-flex align-items-center mt-2 mt-md-0">
            <div className="custom-control custom-checkbox ml-0">
              <input
                type="checkbox"
                className="custom-control-input"
                id="filterHasImage"
                checked={filterHasImage}
                onChange={(e) => setFilterHasImage(e.target.checked)}
              />
              <label className="custom-control-label" htmlFor="filterHasImage" style={{ cursor: 'pointer' }}>
                Show only questions with image
              </label>
            </div>
          </Col>
          {/* Difficulty filter hidden for now */}
        </Row>
        <Row className="mb-4">
          {/* Quality filter hidden for now
          <Col xs="12" md="6" className="mb-3 mb-md-0">
            <FormGroup className="mb-0">
              <Label className="form-control-label">
                Filter by Quality
              </Label>
              <Select
                options={[
                  { value: '', label: 'All Qualities' },
                  { value: 'simple', label: 'Simple' },
                  { value: 'excellent', label: 'Excellent' }
                ]}
                value={
                  filterQuality
                    ? { value: filterQuality, label: filterQuality.charAt(0).toUpperCase() + filterQuality.slice(1) }
                    : { value: '', label: 'All Qualities' }
                }
                onChange={(option) => setFilterQuality(option.value)}
                styles={customDropdownWithBorder}
                placeholder="All Qualities"
              />
            </FormGroup>
          </Col>
          */}
          <Col xs="12" md="3" className="mb-3 mb-md-0">
            <FormGroup className="mb-0">
              <Label className="form-control-label">
                Sort By
              </Label>
              <Select
                options={[
                  { value: 'newest', label: 'Newest First' },
                  { value: 'oldest', label: 'Oldest First' },
                  { value: 'no_topic_first', label: 'No Topic First' },
                  { value: 'title_asc', label: 'Title (A-Z)' },
                  { value: 'title_desc', label: 'Title (Z-A)' },
                  { value: 'most_questions', label: 'Most Questions' },
                  { value: 'least_questions', label: 'Least Questions' }
                ]}
                value={{ value: sortBy, label: {
                  'newest': 'Newest First',
                  'oldest': 'Oldest First',
                  'no_topic_first': 'No Topic First',
                  'title_asc': 'Title (A-Z)',
                  'title_desc': 'Title (Z-A)',
                  'most_questions': 'Most Questions',
                  'least_questions': 'Least Questions'
                }[sortBy] }}
                onChange={(option) => setSortBy(option?.value || 'newest')}
                styles={customDropdownWithBorder}
              />
            </FormGroup>
          </Col>
          <Col xs="12" md="3" className="mb-3 mb-md-0">
            <FormGroup className="mb-0">
              <Label className="form-control-label mb-2" style={{ whiteSpace: 'nowrap' }}>
                Min. Answered
              </Label>
              <Input
                type="number"
                placeholder="e.g. 30"
                min="0"
                value={filterMinAnswered}
                onChange={(e) => setFilterMinAnswered(e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col xs="12" md="6" className="mb-3 mb-md-0">
            <FormGroup className="mb-0">
              <div className="d-flex align-items-center mb-2">
                <Label className="form-control-label mb-0 mr-2" style={{ whiteSpace: 'nowrap' }}>
                  % Correct
                </Label>
                <UncontrolledDropdown>
                  <DropdownToggle caret color="primary" style={{ padding: '0 8px', fontSize: '0.7rem', lineHeight: '1.5', height: 'auto' }}>
                    {{ 'range': 'Range', 'greater': 'Greater than', 'less': 'Less than' }[filterPercentCorrectMode]}
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem active={filterPercentCorrectMode === 'range'} onClick={() => { setFilterPercentCorrectMode('range'); setFilterPercentCorrectMin(''); setFilterPercentCorrectMax(''); setFilterPercentCorrectValue(''); }}>
                      Range
                    </DropdownItem>
                    <DropdownItem active={filterPercentCorrectMode === 'greater'} onClick={() => { setFilterPercentCorrectMode('greater'); setFilterPercentCorrectMin(''); setFilterPercentCorrectMax(''); setFilterPercentCorrectValue(''); }}>
                      Greater than
                    </DropdownItem>
                    <DropdownItem active={filterPercentCorrectMode === 'less'} onClick={() => { setFilterPercentCorrectMode('less'); setFilterPercentCorrectMin(''); setFilterPercentCorrectMax(''); setFilterPercentCorrectValue(''); }}>
                      Less than
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
              {filterPercentCorrectMode && filterPercentCorrectMode !== 'range' && (
                <Input
                  type="number"
                  placeholder="e.g. 70"
                  min="0"
                  max="100"
                  value={filterPercentCorrectValue}
                  onChange={(e) => setFilterPercentCorrectValue(e.target.value)}
                />
              )}
              {filterPercentCorrectMode === 'range' && (
                <div className="d-flex align-items-center">
                  <Input
                    type="number"
                    placeholder="Min"
                    min="0"
                    max="100"
                    value={filterPercentCorrectMin}
                    onChange={(e) => setFilterPercentCorrectMin(e.target.value)}
                  />
                  <span className="mx-2 text-muted">-</span>
                  <Input
                    type="number"
                    placeholder="Max"
                    min="0"
                    max="100"
                    value={filterPercentCorrectMax}
                    onChange={(e) => setFilterPercentCorrectMax(e.target.value)}
                  />
                </div>
              )}
            </FormGroup>
          </Col>
        </Row>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="mb-3">
            <Button
              color="warning"
              size="sm"
              onClick={handleClearFilters}
            >
              <i className="fas fa-times mr-2"></i>
              Clear Filters
            </Button>
          </div>
        )}

        {/* Questions Display */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : filteredQuestions.length === 0 ? (
          <Alert color="info">
            No questions found. Create your first question above!
          </Alert>
        ) : (
          <>
            {/* Passage Groups Section */}
            {Object.keys(groupedQuestions.passageGroups).length > 0 && (
              <>
                <Row className="mb-2 align-items-center">
                  <Col xs="12" md="6" className="mb-2 mb-md-0">
                    <h4 className="mb-1">
                      <i className="fas fa-book-open mr-2 text-info"></i>
                      Passage-Based Questions
                      <Badge color="info" pill className="ml-2">
                        {passageCount} passage{passageCount !== 1 ? 's' : ''}
                      </Badge>
                    </h4>
                    <small className="text-muted">
                      <i className="fas fa-lightbulb mr-1"></i>
                      Recommended: 5-7 questions per passage
                    </small>
                  </Col>
                  <Col xs="12" md="6">
                    <Input
                      type="text"
                      placeholder="Search by title, subject, unit, tag..."
                      value={passageSearch}
                      onChange={(e) => {
                        setPassageSearch(e.target.value);
                        setPassagePage(1);
                      }}
                    />
                  </Col>
                </Row>
                <div className="d-flex justify-content-end mb-3 mt-2">
                  <Button
                    color="outline-primary"
                    size="sm"
                    onClick={expandAllPassages}
                    className="mr-2"
                  >
                    <i className="fas fa-expand-alt mr-1"></i>
                    Expand All
                  </Button>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={collapseAllPassages}
                  >
                    <i className="fas fa-compress-alt mr-1"></i>
                    Collapse All
                  </Button>
                </div>

                {passageSearch && passageCount === 0 ? (
                  <Alert color="warning" className="mb-3">
                    <i className="fas fa-search mr-2"></i>
                    No passages found matching "{passageSearch}"
                  </Alert>
                ) : paginatedPassages.map(passageId => {
                  const group = groupedQuestions.passageGroups[passageId];
                  const passage = group.passage;
                  const passageQuestions = group.questions;
                  const isExpanded = expandedPassages[passageId];

                  if (!passage) return null;

                  return (
                    <Card key={passageId} className="mb-3">
                      <CardBody className="p-3">
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start">
                          <div className="flex-grow-1 mb-2 mb-sm-0">
                            <h5 className="mb-2" style={{ fontSize: '1.1rem' }}>
                              <i className="fas fa-book-open mr-2 text-info"></i>
                              {passage.title}
                            </h5>
                            <div className="mb-2 d-flex flex-wrap">
                              <Badge color="primary" className="mr-2 mb-1">{getSubjectName(passage.subjectId)}</Badge>
                              <Badge color="primary" className="mr-2 mb-1">{getUnitName(passage.unitId)}</Badge>
                              <Badge color="info" className="mr-2 mb-1">
                                {passageQuestions.length} question{passageQuestions.length !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                            <div className="text-muted small">
                              {passage.createdAt && (
                                <span className="mr-3">
                                  <i className="fas fa-calendar-plus mr-1"></i>
                                  Created: {formatDate(passage.createdAt)}
                                </span>
                              )}
                              {passage.updatedAt && (
                                <span>
                                  <i className="fas fa-calendar-check mr-1"></i>
                                  Updated: {formatDate(passage.updatedAt)}
                                </span>
                              )}
                            </div>
                            {!isExpanded && (
                              <div className="text-muted small d-none d-sm-block">
                                <i className="fas fa-info-circle mr-1"></i>
                                Click "View Details" to see passage text and questions
                              </div>
                            )}
                          </div>
                          <div className="align-self-stretch align-self-sm-start">
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => togglePassage(passageId)}
                              block
                              outline={!isExpanded}
                            >
                              <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} mr-1`}></i>
                              {isExpanded ? 'Hide' : 'View'}
                              <span className="d-none d-sm-inline"> Details</span>
                            </Button>
                          </div>
                        </div>

                        {isExpanded && (
                          <>
                            <hr />
                            <div className="border rounded mb-3">
                              <div className="p-3" style={{ height: `${passageHeights[passageId] || 200}px`, overflow: 'auto' }}>
                                {renderQuestionPreview(passage.passageText)}
                              </div>
                              <div
                                className="d-flex align-items-center justify-content-center border-top"
                                style={{
                                  backgroundColor: '#e9ecef',
                                  padding: '8px 0',
                                  cursor: 'ns-resize',
                                  touchAction: 'none'
                                }}
                                onMouseDown={(e) => handlePassageResize(passageId, e)}
                                onTouchStart={(e) => handlePassageResize(passageId, e)}
                              >
                                <div
                                  style={{
                                    width: '60px',
                                    height: '6px',
                                    backgroundColor: '#adb5bd',
                                    borderRadius: '3px'
                                  }}
                                />
                              </div>
                            </div>

                            <h6 className="mb-3 text-muted font-weight-bold">
                              <i className="fas fa-grip-vertical mr-2 text-muted"></i>
                              Questions for this passage (drag to reorder):
                            </h6>

                            {[...passageQuestions].sort((a, b) => (a.passageOrder || 0) - (b.passageOrder || 0)).map((question, qIndex) => (
                      <Card
                        key={question.questionId}
                        className={`mb-2 border ${dragOverQuestion === question.questionId ? 'border-primary' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, question.questionId, passageId)}
                        onDragOver={(e) => handleDragOver(e, question.questionId)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, question.questionId, passageId)}
                        onDragEnd={handleDragEnd}
                        style={{ cursor: 'grab', transition: 'border-color 0.2s' }}
                      >
                        <CardBody className="p-3">
                          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start">
                            <div className="flex-grow-1 mb-2 mb-sm-0">
                              <div className="mb-2 d-flex flex-wrap">
                                <Badge color="danger" className="mr-2 mb-1">Q{qIndex + 1}</Badge>
                                {question.questionCode && (
                                  <Badge color="primary" className="mr-2 mb-1">{question.questionCode}</Badge>
                                )}
                                {getTopicName(question.topicId) && (
                                  <Badge color="primary" pill className="mr-2 mb-1">
                                    {getTopicName(question.topicId)}
                                  </Badge>
                                )}
                                {/* Difficulty badge hidden for now */}
                                {/* Quality badge hidden for now
                                <Badge color={getQualityColor(question.quality)} pill className="mr-2 mb-1">
                                  {question.quality}
                                </Badge>
                                */}
                                <span className="text-muted small">{question.choices?.length || 0} choices</span>
                                {(question.timesAnswered || 0) >= 10 && (
                                  <Badge color={(() => { const pct = Math.round(((question.timesCorrect || 0) / question.timesAnswered) * 100); return pct >= 90 ? 'success' : pct >= 70 ? 'info' : pct >= 50 ? 'warning' : 'danger'; })()} className="ml-2 mb-1">
                                    {Math.round(((question.timesCorrect || 0) / question.timesAnswered) * 100)}% correct ({question.timesCorrect}/{question.timesAnswered})
                                  </Badge>
                                )}
                              </div>
                              <div style={{ maxHeight: '60px', overflow: 'hidden' }}>
                                {renderQuestionPreview(question.questionText)}
                              </div>
                              <div className="text-muted small mt-2">
                                {question.createdAt && (
                                  <span className="mr-3">
                                    <i className="fas fa-calendar-plus mr-1"></i>
                                    Created: {formatDate(question.createdAt)}
                                  </span>
                                )}
                                {question.updatedAt && (
                                  <span>
                                    <i className="fas fa-calendar-check mr-1"></i>
                                    Updated: {formatDate(question.updatedAt)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="d-flex flex-row flex-sm-column flex-md-row">
                              <Button
                                color="primary"
                                size="sm"
                                id={`toggler-${question.questionId}`}
                                onClick={() => toggleQuestion(question.questionId)}
                                title={expandedQuestions[question.questionId] ? "Collapse" : "View Details"}
                                className="mr-2 mb-1"
                              >
                                <i className={`fas fa-eye${expandedQuestions[question.questionId] ? '-slash' : ''}`}></i>
                              </Button>
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => onEditQuestion(question)}
                                title="Edit"
                                className="mr-2 mb-1"
                              >
                                <i className="fas fa-edit"></i>
                              </Button>
                              <Button
                                color="warning"
                                size="sm"
                                onClick={() => handleDelete(question.questionId)}
                                title="Archive"
                                className="mb-1"
                              >
                                <i className="fas fa-archive"></i>
                              </Button>
                            </div>
                          </div>

                          <UncontrolledCollapse toggler={`#toggler-${question.questionId}`}>
                            <hr className="my-3" />
                            <div>
                              <h6 className="text-uppercase text-muted font-weight-bold mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                <i className="fas fa-question-circle mr-2"></i>Full Question
                              </h6>
                              {renderQuestionPreview(question.questionText)}

                              <h6 className="text-uppercase text-muted font-weight-bold mt-4 mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                <i className="fas fa-list-ul mr-2"></i>Choices
                              </h6>
                              {question.choices?.map((choice, idx) => (
                                <div key={idx} className="mb-2 p-3 border rounded" style={{ backgroundColor: question.correctAnswer?.includes(choice.choiceId) ? '#d4edda' : '#f8f9fa', display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                                  <div style={{ marginRight: '12px', flexShrink: 0 }}>
                                    <Badge
                                      color={question.correctAnswer?.includes(choice.choiceId) ? 'success' : 'secondary'}
                                      pill
                                      style={{
                                        minWidth: '32px',
                                        height: '32px',
                                        fontSize: '0.9rem',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0'
                                      }}
                                    >
                                      {question.correctAnswer?.includes(choice.choiceId) ? '✓' : getChoiceLabel(idx)}
                                    </Badge>
                                  </div>
                                  <div style={{ flex: 1, wordBreak: 'break-word', minWidth: 0 }}>
                                    {renderQuestionPreview(choice.text)}
                                  </div>
                                </div>
                              ))}

                              <h6 className="text-uppercase text-muted font-weight-bold mt-4 mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                <i className="fas fa-lightbulb mr-2"></i>Explanation
                              </h6>
                              {renderQuestionPreview(question.explanationText)}

                              {question.tags && question.tags.length > 0 && (
                                <>
                                  <h6 className="text-uppercase text-muted font-weight-bold mt-4 mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                    <i className="fas fa-tags mr-2"></i>Tags
                                  </h6>
                                  {question.tags.map((tag, idx) => (
                                    <Badge key={idx} color="info" className="mr-2">
                                      {tag}
                                    </Badge>
                                  ))}
                                </>
                              )}
                            </div>
                          </UncontrolledCollapse>
                        </CardBody>
                      </Card>
                    ))}
                          </>
                        )}
                      </CardBody>
                    </Card>
                  );
                })}

                {/* Pagination Controls for Passages */}
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3">
                  <div className="text-muted small mb-2 mb-sm-0">
                    Showing {((passagePage - 1) * passagesPerPage) + 1} to {Math.min(passagePage * passagesPerPage, passageCount)} of {passageCount} passages
                  </div>
                  <div className="d-flex align-items-center">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setPassagePage(prev => Math.max(1, prev - 1))}
                      disabled={passagePage === 1 || totalPassagePages <= 1}
                      className="mr-2"
                    >
                      <i className="fas fa-chevron-left"></i>
                      <span className="d-none d-sm-inline ml-1">Previous</span>
                    </Button>
                    <span className="mx-2 text-nowrap">
                      Page {passagePage} of {totalPassagePages}
                    </span>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setPassagePage(prev => Math.min(totalPassagePages, prev + 1))}
                      disabled={passagePage === totalPassagePages || totalPassagePages <= 1}
                      className="ml-2"
                    >
                      <span className="d-none d-sm-inline mr-1">Next</span>
                      <i className="fas fa-chevron-right"></i>
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Standalone Questions */}
            {groupedQuestions.standaloneQuestions.length > 0 && (
              <>
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3 mt-5">
                  <h4 className="mb-2 mb-sm-0">
                    <i className="fas fa-question-circle mr-2 text-info"></i>
                    Standalone Questions
                    <Badge color="info" pill className="ml-2">
                      {filteredStandaloneQuestions.length} Question{filteredStandaloneQuestions.length !== 1 ? 's' : ''}
                    </Badge>
                  </h4>
                  <Input
                    type="text"
                    placeholder="Search by title, subject, unit, tag..."
                    value={standaloneSearch}
                    onChange={(e) => {
                      setStandaloneSearch(e.target.value);
                      setStandalonePage(1);
                    }}
                    className="mt-2 mt-sm-0"
                    style={{ width: '280px' }}
                  />
                </div>
                {standaloneSearch && filteredStandaloneQuestions.length === 0 ? (
                  <Alert color="warning">
                    <i className="fas fa-search mr-2"></i>
                    No standalone questions found matching "{standaloneSearch}"
                  </Alert>
                ) : (
                <div className="standalone-questions-list">
                  {paginatedStandaloneQuestions.map((question) => (
                    <Card key={question.questionId} className="mb-2 border">
                      <CardBody className="p-3">
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start">
                          <div className="flex-grow-1 mb-2 mb-sm-0">
                            <div className="mb-2 d-flex flex-wrap">
                              {question.questionCode && (
                                <Badge color="primary" className="mr-2 mb-1">{question.questionCode}</Badge>
                              )}
                              <Badge color="primary" className="mr-2 mb-1">{getSubjectName(question.subjectId)}</Badge>
                              <Badge color="primary" className="mr-2 mb-1">{getUnitName(question.unitId)}</Badge>
                              {getTopicName(question.topicId) && (
                                <Badge color="primary" pill className="mr-2 mb-1">
                                  {getTopicName(question.topicId)}
                                </Badge>
                              )}
                              {/* Difficulty badge hidden for now */}
                              {/* Quality badge hidden for now
                              <Badge color={getQualityColor(question.quality)} pill className="mr-2 mb-1">
                                {question.quality}
                              </Badge>
                              */}
                              <span className="text-muted small">{question.choices?.length || 0} choices</span>
                              {(question.timesAnswered || 0) >= 10 && (
                                <Badge color={(() => { const pct = Math.round(((question.timesCorrect || 0) / question.timesAnswered) * 100); return pct >= 90 ? 'success' : pct >= 70 ? 'info' : pct >= 50 ? 'warning' : 'danger'; })()} className="ml-2 mb-1">
                                  {Math.round(((question.timesCorrect || 0) / question.timesAnswered) * 100)}% correct ({question.timesCorrect}/{question.timesAnswered})
                                </Badge>
                              )}
                            </div>
                            <div style={{ maxHeight: '60px', overflow: 'hidden' }}>
                              {renderQuestionPreview(question.questionText)}
                            </div>
                            <div className="text-muted small mt-2">
                              {question.createdAt && (
                                <span className="mr-3">
                                  <i className="fas fa-calendar-plus mr-1"></i>
                                  Created: {formatDate(question.createdAt)}
                                </span>
                              )}
                              {question.updatedAt && (
                                <span>
                                  <i className="fas fa-calendar-check mr-1"></i>
                                  Updated: {formatDate(question.updatedAt)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="d-flex flex-row flex-sm-column flex-md-row">
                            <Button
                              color="primary"
                              size="sm"
                              id={`toggler-${question.questionId}`}
                              onClick={() => toggleQuestion(question.questionId)}
                              title={expandedQuestions[question.questionId] ? "Collapse" : "View Details"}
                              className="mr-2 mb-1"
                            >
                              <i className={`fas fa-eye${expandedQuestions[question.questionId] ? '-slash' : ''}`}></i>
                            </Button>
                            <Button
                              color="info"
                              size="sm"
                              onClick={() => onEditQuestion(question)}
                              title="Edit"
                              className="mr-2 mb-1"
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            <Button
                              color="warning"
                              size="sm"
                              onClick={() => handleDelete(question.questionId)}
                              title="Archive"
                              className="mb-1"
                            >
                              <i className="fas fa-archive"></i>
                            </Button>
                          </div>
                        </div>

                        <UncontrolledCollapse toggler={`#toggler-${question.questionId}`}>
                          <hr className="my-3" />
                          <div>
                            <h6 className="text-uppercase text-muted font-weight-bold mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                              <i className="fas fa-question-circle mr-2"></i>Full Question
                            </h6>
                            {renderQuestionPreview(question.questionText)}

                            <h6 className="text-uppercase text-muted font-weight-bold mt-4 mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                              <i className="fas fa-list-ul mr-2"></i>Choices
                            </h6>
                            {question.choices?.map((choice, idx) => (
                              <div key={idx} className="mb-2 p-3 border rounded" style={{ backgroundColor: question.correctAnswer?.includes(choice.choiceId) ? '#d4edda' : '#f8f9fa', display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                                <div style={{ marginRight: '12px', flexShrink: 0 }}>
                                  <Badge
                                    color={question.correctAnswer?.includes(choice.choiceId) ? 'success' : 'secondary'}
                                    pill
                                    style={{
                                      minWidth: '32px',
                                      height: '32px',
                                      fontSize: '0.9rem',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      padding: '0'
                                    }}
                                  >
                                    {question.correctAnswer?.includes(choice.choiceId) ? '✓' : getChoiceLabel(idx)}
                                  </Badge>
                                </div>
                                <div style={{ flex: 1, wordBreak: 'break-word', minWidth: 0 }}>
                                  {renderQuestionPreview(choice.text)}
                                </div>
                              </div>
                            ))}

                            <h6 className="text-uppercase text-muted font-weight-bold mt-4 mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                              <i className="fas fa-lightbulb mr-2"></i>Explanation
                            </h6>
                            {renderQuestionPreview(question.explanationText)}

                            {question.tags && question.tags.length > 0 && (
                              <>
                                <h6 className="text-uppercase text-muted font-weight-bold mt-4 mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                  <i className="fas fa-tags mr-2"></i>Tags
                                </h6>
                                {question.tags.map((tag, idx) => (
                                  <Badge key={idx} color="info" className="mr-2">
                                    {tag}
                                  </Badge>
                                ))}
                              </>
                            )}
                          </div>
                        </UncontrolledCollapse>
                      </CardBody>
                    </Card>
                  ))}
                </div>
                )}

                {/* Pagination Controls */}
                {filteredStandaloneQuestions.length > 0 && (
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3">
                  <div className="text-muted small mb-2 mb-sm-0">
                    Showing {((standalonePage - 1) * standalonePerPage) + 1} to {Math.min(standalonePage * standalonePerPage, filteredStandaloneQuestions.length)} of {filteredStandaloneQuestions.length} questions
                  </div>
                  <div className="d-flex align-items-center">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setStandalonePage(prev => Math.max(1, prev - 1))}
                      disabled={standalonePage === 1 || totalStandalonePages <= 1}
                      className="mr-2"
                    >
                      <i className="fas fa-chevron-left"></i>
                      <span className="d-none d-sm-inline ml-1">Previous</span>
                    </Button>
                    <span className="mx-2 text-nowrap">
                      Page {standalonePage} of {totalStandalonePages}
                    </span>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setStandalonePage(prev => Math.min(totalStandalonePages, prev + 1))}
                      disabled={standalonePage === totalStandalonePages || totalStandalonePages <= 1}
                      className="ml-2"
                    >
                      <span className="d-none d-sm-inline mr-1">Next</span>
                      <i className="fas fa-chevron-right"></i>
                    </Button>
                  </div>
                </div>
                )}
              </>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default QuestionsList;
